// Signing in to the brand workspace, which is also how a brand account comes
// to exist: any work email gets a six-digit code and a one-time link.
//
// The code page follows what people know from other products (Brúnó,
// 2026-10-11): a box per digit, the form sends itself on the sixth, a wrong
// code clears and says so, and a new code can be asked for after a short wait.
//
// A link from the mail lands here with ?token=, but signs in only after the
// page says whose workspace it opens and the reader clicks: a link that signed
// in on arrival would let whoever sent it pick the workspace its reader lands in.

import {useEffect, useRef, useState} from 'react';
import type {FormEvent} from 'react';
import {Navigate, useNavigate, useSearchParams} from 'react-router-dom';
import {CODE_LENGTH, CodeInput} from '../creator/components/CodeInput';
import {Icon} from '../creator/components/Icon';
import {BrandApiError, describe, peekLogin, redeemLogin, requestLogin, verifyLogin} from './api';
import {usePageTitle} from './page-title';
import {useBrand} from './store';

/** How long before another code can be asked for: the server allows five an hour. */
const RESEND_SECONDS = 30;

export function SignInPage() {
  const {status, enter} = useBrand();
  usePageTitle('Sign in');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // An invitation opens this page with the invited address filled in.
  const [invited] = useState(Boolean(params.get('email')));
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [resent, setResent] = useState(false);
  const [wait, setWait] = useState(0);
  const [devCode, setDevCode] = useState('');
  const [link, setLink] = useState(params.get('token') ?? '');
  const [linkEmail, setLinkEmail] = useState('');
  const peeked = useRef(false);

  useEffect(() => {
    if (!link || peeked.current) return;
    peeked.current = true; setBusy(true);
    setParams({}, {replace: true});
    peekLogin(link)
      .then((peek) => { if (peek.current) navigate('/', {replace: true}); else setLinkEmail(peek.email); })
      .catch((caught) => { setError(describe(caught)); setLink(''); })
      .finally(() => setBusy(false));
  }, [link, navigate, setParams]);

  useEffect(() => {
    if (wait <= 0) return;
    const timer = window.setTimeout(() => setWait((left) => left - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [wait]);

  if (status === 'ready' && !link) return <Navigate to="/" replace />;

  async function openLink() {
    setBusy(true); setError('');
    try { await redeemLogin(link); await enter(); navigate('/', {replace: true}); }
    catch (caught) { setError(describe(caught)); setLink(''); setLinkEmail(''); }
    finally { setBusy(false); }
  }

  async function send(again: boolean) {
    setBusy(true); setError(''); setCodeError('');
    try {
      const result = await requestLogin(email.trim());
      setSent(true); setResent(again); setCode(''); setWait(RESEND_SECONDS);
      setDevCode(result.dev_code || '');
    } catch (caught) {
      if (again) setCodeError(describe(caught)); else setError(describe(caught));
    } finally { setBusy(false); }
  }

  async function verify(typed: string) {
    if (busy || typed.length !== CODE_LENGTH) return;
    setBusy(true); setCodeError(''); setResent(false);
    try { await verifyLogin(email.trim(), typed); await enter(); }
    catch (caught) {
      // Cleared rather than left in the boxes: the next try is typed from the start.
      setCode('');
      setCodeError(caught instanceof BrandApiError && caught.code === 'invalid_code'
        ? 'That code did not work. Check that it is from the newest email, or send a new one.'
        : describe(caught));
    } finally { setBusy(false); }
  }

  function changeEmail() {
    setSent(false); setCode(''); setCodeError(''); setResent(false); setDevCode('');
  }

  return <div className="brand-signin">
    <section className="brand-signin-side">
      <a className="brand" href="/en/"><img src="/img/logo-mark.webp?v=4" alt="" />earnby<em>content</em></a>
      <div><p className="eyebrow">For brands</p><h2>Creator campaigns,<br />without the chaos.</h2><p>Build the brief, set the offer and follow every collaboration from one calm workspace.</p>
        <ul><li><Icon name="check" />Clear campaign setup</li><li><Icon name="spark" />Rate guidance before you publish</li><li><Icon name="chat" />Protected creator conversations</li></ul>
      </div>
    </section>
    <main className="brand-signin-main"><section className="signin-box">
      {link ? <>
        <div><p className="eyebrow">Brand workspace</p><h1>{linkEmail ? `Sign in as ${linkEmail}?` : 'Opening your link…'}</h1></div>
        {linkEmail ? <>
          <p className="muted">This link opens that workspace in this browser. Continue only if the address is yours{status === 'ready' ? '; it signs you out of the workspace open now' : ''}.</p>
          <button className="btn btn-g" type="button" disabled={busy} onClick={() => void openLink()}>{busy ? 'Opening…' : 'Open workspace'}<Icon name="arrowRight" size={17} /></button>
          <button className="text-button" type="button" onClick={() => { setLink(''); setLinkEmail(''); }}>{status === 'ready' ? 'Stay in the workspace open now' : 'Use another email'}</button>
        </> : null}
      </> : sent ? <>
        <div className="signin-mail-mark"><Icon name="mail" size={26} /></div>
        <div><h1>Check your inbox</h1>
          <p className="muted signin-lead">Enter the 6-digit code we sent to <b>{email.trim()}</b>.{' '}
            <button className="link-button" type="button" onClick={changeEmail}>Change</button></p></div>
        {devCode ? <p className="dev-code-row"><span>Local development, no mail sent:</span><code className="dev-code">{devCode}</code></p> : null}
        <form className="signin-code-form" onSubmit={(event: FormEvent) => { event.preventDefault(); void verify(code); }}>
          <CodeInput label="Sign-in code" value={code} autoFocus disabled={busy} invalid={Boolean(codeError)}
            onChange={(next) => { setCode(next); if (codeError) setCodeError(''); }}
            onComplete={(typed) => void verify(typed)} />
          {codeError ? <p className="code-message error" role="alert"><Icon name="alert" size={16} />{codeError}</p>
            : resent ? <p className="code-message" role="status"><Icon name="check" size={16} />New code sent. Earlier codes still work until they expire.</p> : null}
          <button className="btn btn-g" type="submit" disabled={busy || code.length !== CODE_LENGTH}>{busy ? 'Checking…' : 'Continue'}</button>
        </form>
        <p className="signin-help">Can't find it? Check your spam folder, or{' '}
          {wait > 0 ? <span className="resend-wait">send a new code in {wait}s</span>
            : <button className="link-button" type="button" disabled={busy} onClick={() => void send(true)}>send a new code</button>}.</p>
      </> : <>
        <div><p className="eyebrow">Brand workspace</p><h1>{invited ? 'Join your team' : 'Sign in or create an account'}</h1>
          <p className="muted signin-lead">{invited ? 'Continue with the address you were invited with. We send it a code, no password needed.' : 'Use your work email. We send you a code, no password needed.'}</p></div>
        <form onSubmit={(event: FormEvent) => { event.preventDefault(); void send(false); }}>
          <label className="brand-field"><span>Work email</span>
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="you@company.com"
              autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} autoFocus required /></label>
          <button className="btn btn-g" type="submit" disabled={busy}>{busy ? 'Sending…' : 'Continue with email'}{busy ? null : <Icon name="arrowRight" size={17} />}</button>
        </form>
      </>}
      {error ? <p className="form-alert" role="alert">{error}</p> : null}
      <p className="signin-legal">By continuing, you agree to our <a href="/terms/">Terms</a> and <a href="/privacy/">Privacy Policy</a>.</p>
    </section></main>
  </div>;
}
